import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Breadcrumbs,
  Link as MUILink,
  Paper,
  Tabs,
  Tab,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Button,
  Stack,
  Alert,
  Skeleton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  FormGroup,
  FormControlLabel,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Home,
  Settings,
  Description,
  Edit,
  Delete,
  Close,
  SupportAgent,
  Inventory2,
  MenuBook,
  Book,
  LocalShipping,
  LocationOn,
  Analytics,
  CloudUpload,
  ContentCopy,
  Add,
  Tune,
} from '@mui/icons-material';
import Checkbox from '@mui/material/Checkbox';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAdminConfigs } from '@/hooks/queries/admin/useAdminConfigs';
import { useUpdateConfig } from '@/hooks/mutations/admin/useUpdateConfig';
import { useCifStamp } from '@/hooks/queries/admin/useCifStamp';
import { useUploadCifStamp, useDeleteCifStamp } from '@/hooks/mutations/admin/useCifStampMutations';
import { useNotifications } from '@/stores/useNotificationStore';
import { useAllEntities, useUpdateEntity, useDeleteEntity, useCreateEntity } from '@/hooks';
import { MODULE_DEFAULTS } from '@/types/moduleConfig';
import { getEnvironmentBanner } from '@/utils/environment';
import { ShelfMapConfigPanel } from '@/components/shelf-map/ShelfMapConfigPanel';
import type { ModuleConfig, ModuleKey } from '@/types/moduleConfig';
import type { SystemConfigRow } from '@/types/systemConfig';
import type { Entity } from '@/types/Entity';
import { useEmailTemplates } from '@/hooks/queries/notifications/useEmailAdmin';
import { useUpdateEmailTemplate } from '@/hooks/mutations/notifications/useEmailTemplateMutations';

// ---------------------------------------------------------------------------
// Auto-save hook — debounces save calls so every keystroke doesn't hit the API
// ---------------------------------------------------------------------------

function useAutoSave(onSave: () => Promise<void>, delay = 1000) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const trigger = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      void onSaveRef.current();
    }, delay);
  }, [delay]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return trigger;
}

// ---------------------------------------------------------------------------
// Reusable auto-save hint shown on every config panel
// ---------------------------------------------------------------------------

const AUTO_SAVE_HINT = 'Press Enter or click away from a field to save.';

const AutoSaveHint = () => (
  <Typography variant="caption" color="text.secondary">
    {AUTO_SAVE_HINT}
  </Typography>
);

// ---------------------------------------------------------------------------
// Document configuration definitions — add new documents here
// ---------------------------------------------------------------------------

interface FieldDef {
  configKey: string;
  label: string;
  helperText?: string;
  multiline?: boolean;
}

interface DocumentDef {
  key: string;
  label: string;
  description: string;
  prefix: string;
  fields: FieldDef[];
}

const DOCUMENT_CONFIGS: DocumentDef[] = [
  {
    key: 'sf1034',
    label: 'SF-1034',
    description:
      'Public Voucher for Purchases and Services Other Than Personal. Controls the Accountable Officer and Comptroller fields printed on generated SF-1034 documents.',
    prefix: 'sf1034_',
    fields: [
      {
        configKey: 'sf1034_approved_by_name',
        label: 'Accountable Officer Name',
        helperText: 'Full name of the approving Accountable Officer',
      },
      {
        configKey: 'sf1034_approved_by_title',
        label: 'Accountable Officer Title',
        helperText: 'Rank and title (e.g. CW4, QM, ACCOUNTABLE OFFICER)',
      },
      {
        configKey: 'sf1034_comptroller_name',
        label: 'Comptroller Name',
        helperText: 'Full name of the Comptroller',
      },
      {
        configKey: 'sf1034_comptroller_title',
        label: 'Comptroller Title',
        helperText: 'Rank and title (e.g. LTC, FC, Comptroller)',
      },
      {
        configKey: 'sf1034_department_bureau_location',
        label: 'Department / Bureau / Location',
        helperText: 'DFAS mailing address block (one line per row)',
        multiline: true,
      },
      {
        configKey: 'sf1034_accounting_classification_default',
        label: 'Accounting Classification Default',
        helperText: 'Default text when no LOA is available on items (e.g. FY LOA ADDED)',
      },
      {
        configKey: 'sf1034_requisition_number_prefix',
        label: 'Requisition Number Prefix',
        helperText: 'Prefix for generated requisition numbers (e.g. REQ)',
      },
    ],
  },
  {
    key: 'da2407',
    label: 'DA 2407',
    description:
      'Maintenance Request. Controls the site-specific header fields printed on generated DA Form 2407 documents.',
    prefix: 'da2407_',
    fields: [
      {
        configKey: 'da2407_hr_number',
        label: 'Hand Receipt Number',
        helperText: 'Hand Receipt number printed on the form header',
      },
      {
        configKey: 'da2407_uic',
        label: 'Unit Identification Code (UIC)',
        helperText: 'Unit Identification Code for the organization',
      },
      {
        configKey: 'da2407_organization_activity',
        label: 'Organization / Activity',
        helperText: 'Organization or Activity name printed on the form',
      },
      {
        configKey: 'da2407_customer_unit',
        label: 'Customer Unit',
        helperText: 'Customer unit phone or identifier',
      },
      {
        configKey: 'da2407_customer_scampi',
        label: 'Customer SCAMPI',
        helperText: 'Customer SCAMPI extension number',
      },
      {
        configKey: 'da2407_phone',
        label: 'Phone Number',
        helperText: 'Primary phone number printed on the form',
      },
    ],
  },
  {
    key: 'da4949',
    label: 'DA 4949',
    description:
      'Administrative Adjustment Report. Controls the Organization/Activity and Approving Official (PBO) fields printed on generated DA4949 documents.',
    prefix: 'da4949_',
    fields: [
      {
        configKey: 'da4949_organization_activity',
        label: 'Organization / Activity',
        helperText: 'Organization or Activity value printed on generated DA4949 forms',
      },
      {
        configKey: 'da4949_approving_official_name',
        label: 'Approving Official (PBO) Name',
        helperText: 'Full name of the accountable officer / PBO',
      },
      {
        configKey: 'da4949_authority_name',
        label: 'Authority Name',
        helperText: 'Regulatory authority reference (e.g. AR 710-2)',
      },
      {
        configKey: 'da4949_default_justification',
        label: 'Default Justification',
        helperText: 'Default justification text when none is provided by the workflow',
      },
    ],
  },
  {
    key: 'da3161',
    label: 'DA 3161',
    description:
      'Request for Issue or Turn-In. Controls the default values for REQ FROM, SEND TO, and the lateral transfer approval line on generated DA3161 documents.',
    prefix: 'da3161_',
    fields: [
      {
        configKey: 'da3161_req_from',
        label: 'REQ FROM (Issue From)',
        helperText: 'Default value for the REQ FROM field (e.g. OCIE, IEM)',
      },
      {
        configKey: 'da3161_send_to',
        label: 'SEND TO',
        helperText: 'Default value for the SEND TO field (e.g. CIF)',
      },
      {
        configKey: 'da3161_lateral_transfer_approved_by',
        label: 'Lateral Transfer Approved By',
        helperText:
          'Approval line printed in the Item Description field (e.g. "Lateral Transfer approved by: Name, Rank, Branch")',
      },
    ],
  },
  {
    key: 'dd1348',
    label: 'DD 1348-1A',
    description:
      'Issue Release/Receipt Document. Controls the default NSN value auto-populated for Z expendable manual entries on the DD 1348-1A.',
    prefix: 'z_expendable_',
    fields: [
      {
        configKey: 'z_expendable_default_nsn',
        label: 'Z Expendable Default NSN',
        helperText: 'Default NSN value for manual Z expendable entries (e.g. 8415-ZEXPEND)',
      },
    ],
  },
  {
    key: 'contamination_memo',
    label: 'Contamination Memo',
    description:
      'Contamination Memorandum. Controls the default Hand Receipt Number printed on generated Contamination Memo documents.',
    prefix: 'contamination_memo_',
    fields: [
      {
        configKey: 'contamination_memo_hand_receipt_number',
        label: 'Hand Receipt Number',
        helperText: 'Default Hand Receipt number printed on the memo',
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Top-level module tabs — each module is a tab on the config page.
// Modules that have dedicated functional panels (documents, locations, support)
// render those panels alongside (or instead of) the label editor.
// ---------------------------------------------------------------------------

/**
 * Config-page sidebar tabs. Order here controls the sidebar display order.
 */
type ConfigTabKey = ModuleKey;

const CONFIG_TABS: { key: ConfigTabKey; label: string; icon: React.ReactElement }[] = [
  { key: 'catalog', label: 'Catalog', icon: <MenuBook fontSize="small" /> },
  { key: 'distribution', label: 'Distribution', icon: <LocalShipping fontSize="small" /> },
  { key: 'documents', label: 'Documents', icon: <Description fontSize="small" /> },
  { key: 'general', label: 'General', icon: <Tune fontSize="small" /> },
  { key: 'iem', label: 'Equipment', icon: <Inventory2 fontSize="small" /> },
  { key: 'locations', label: 'Locations', icon: <LocationOn fontSize="small" /> },
  { key: 'reports', label: 'Reports', icon: <Analytics fontSize="small" /> },
  { key: 'supply', label: 'Supply', icon: <Book fontSize="small" /> },
  { key: 'support', label: 'Support', icon: <SupportAgent fontSize="small" /> },
];

// ---------------------------------------------------------------------------
// Per-document settings form
// ---------------------------------------------------------------------------

const DocumentConfigForm: React.FC<{
  doc: DocumentDef;
  rows: SystemConfigRow[];
  onSaved: () => void;
}> = ({ doc, rows, onSaved }) => {
  const { mutateAsync: updateConfig } = useUpdateConfig();
  const notifications = useNotifications();

  // Build initial form state from DB rows
  const buildFormState = useCallback(() => {
    const state: Record<string, string> = {};
    for (const field of doc.fields) {
      const row = rows.find((r) => r.config_key === field.configKey);
      const raw = row?.config_value;
      state[field.configKey] = typeof raw === 'string' ? raw : '';
    }
    return state;
  }, [doc.fields, rows]);

  const [form, setForm] = useState<Record<string, string>>(buildFormState);

  // Resync when rows change (e.g. after refetch)
  useEffect(() => {
    setForm(buildFormState());
  }, [buildFormState]);

  const triggerSave = useAutoSave(async () => {
    try {
      for (const field of doc.fields) {
        const currentValue = form[field.configKey] ?? '';
        const originalRow = rows.find((r) => r.config_key === field.configKey);
        const originalValue =
          typeof originalRow?.config_value === 'string' ? originalRow.config_value : '';
        if (currentValue !== originalValue) {
          await updateConfig({ configKey: field.configKey, configValue: currentValue });
        }
      }
      notifications.success(`${doc.label} configuration saved`);
      onSaved();
    } catch {
      notifications.error(`Failed to save ${doc.label} configuration`);
    }
  });

  const handleChange = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    triggerSave();
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5">{doc.label} Settings</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {doc.description}
          </Typography>
          <AutoSaveHint />
        </Box>

        <Alert severity="info" variant="outlined">
          Changes apply to newly generated documents only. Previously generated documents are not
          affected.
        </Alert>

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          }}
        >
          {doc.fields.map((field) => (
            <TextField
              key={field.configKey}
              label={field.label}
              helperText={field.helperText}
              fullWidth
              value={form[field.configKey] ?? ''}
              onChange={(e) => handleChange(field.configKey, e.target.value)}
              multiline={field.multiline}
              minRows={field.multiline ? 2 : undefined}
            />
          ))}
        </Box>
      </Stack>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Screen Labels panel - label editor for the selected module
// ---------------------------------------------------------------------------

/** Human-readable label key names for display. */
const LABEL_DISPLAY_NAMES: Record<string, string> = {
  menu_label: 'Navigation Menu Label',
  menu_label_service_member: 'Navigation Menu Label (Service Member)',
  conflicting_roles_message: 'Conflicting Roles Message',
  issue_workflow_title: 'Issue Workflow Title',
  turn_in_workflow_title: 'Turn-In Workflow Title',
  exchange_workflow_title: 'Exchange Workflow Title',
  main_content_tabs_label: 'Main Content Tabs Label (accessibility only)',
  person_label: 'Person Label (singular)',
  person_label_plural: 'Person Label (plural)',
  individual_id_label: 'Individual ID Label',
  individual_id_prefix: 'Individual ID Prefix',
  front_window_person_label: 'Person Label (singular)',
  front_window_person_label_plural: 'Person Label (plural)',
  reports_person_label: 'Person Label (singular)',
  reports_person_label_plural: 'Person Label (plural)',
  global_search_person_label: 'Person Label (singular)',
  global_search_person_label_plural: 'Person Label (plural)',
  assigned_stock_label: 'Assigned Stock Label',
  available_stock_label: 'Available Stock Label',
  asset_authority_default: 'Asset Authority Default',
  asset_authority_helper: 'Asset Authority Helper Text',
  issue_from_default: 'Issue From Default',
  req_from_default: 'Request From Default',
  send_to_default: 'Send To Default',
  block9_label: 'Block 9 Label',
  iem_field_label: 'IEM Field Label',
  issue_facility_label: 'Designation Label',
  issue_facility_help: 'Designation Help Text',
  description: 'Description',
  report_footer_text: 'Report Footer Text',
  ie_kit_label: 'Individual Equipment Kit Label (singular)',
  ie_kit_label_plural: 'Individual Equipment Kit Label (plural)',
  ie_kit_id_prefix: 'Kit Identifier Prefix',
  front_window_tab_label: 'Tab Label',
  front_window_column_label: 'Column Header Label',
  reports_label: 'Individual Equipment Kit Label (singular)',
  reports_label_plural: 'Individual Equipment Kit Label (plural)',
  global_search_label: 'Individual Equipment Kit Label (singular)',
  global_search_label_plural: 'Individual Equipment Kit Label (plural)',
  primary_label: 'Primary Location Category Label',
  secondary_label: 'Secondary Location Category Label',
  related_label: 'Related Location Category Label',
  restricted_tag_label: 'Restricted Tag Label',
  ie_kit_changes_label: 'IE Kit Changes Label',
  buy_program_label: 'Buy Program Label',
  restricted_program_label: 'Restricted Program Label',
  restricted_program_issue_label: 'Restricted Program Issue Label',
  label: 'Position Assignment Label',
};

/**
 * Tab definitions for the Individual Equipment Settings panel.
 * Each tab specifies which composite keys to render. Keys prefixed with "ie_kits."
 * pull from the ie_kits feature; keys prefixed with "front_window." pull from front_window.
 */
const IEM_TAB_DEFS: {
  key: string;
  labelFormKey?: string;
  defaultLabel: string;
  fields: string[];
}[] = [
  {
    key: 'front_window',
    labelFormKey: 'front_window.menu_label',
    defaultLabel: 'Front Window',
    fields: [
      'front_window.menu_label',
      'front_window.menu_label_service_member',
      'front_window.conflicting_roles_message',
      'front_window.issue_workflow_title',
      'front_window.turn_in_workflow_title',
      'front_window.exchange_workflow_title',
      'front_window.main_content_tabs_label',
      'ie_kits.front_window_tab_label',
      'ie_kits.front_window_column_label',
      'identity.front_window_person_label',
      'identity.front_window_person_label_plural',
      'location_categories.primary_label',
      'location_categories.secondary_label',
      'location_categories.related_label',
    ],
  },
  {
    key: 'reports',
    defaultLabel: 'Reports',
    fields: [
      'ie_kits.reports_label',
      'ie_kits.reports_label_plural',
      'identity.reports_person_label',
      'identity.reports_person_label_plural',
    ],
  },
  {
    key: 'global_search',
    defaultLabel: 'Global Search',
    fields: [
      'ie_kits.global_search_label',
      'ie_kits.global_search_label_plural',
      'identity.global_search_person_label',
      'identity.global_search_person_label_plural',
    ],
  },
  {
    key: 'ie_kits',
    labelFormKey: 'ie_kits.menu_label',
    defaultLabel: 'Individual Equipment Kit Management',
    fields: [
      'ie_kits.menu_label',
      'ie_kits.ie_kit_label',
      'ie_kits.ie_kit_label_plural',
      'ie_kits.ie_kit_id_prefix',
      'ie_kits.restricted_tag_label',
    ],
  },
  {
    key: 'issue_programs',
    defaultLabel: 'Issue Programs',
    fields: [
      'issue_programs.buy_program_label',
      'issue_programs.restricted_program_label',
      'issue_programs.restricted_program_issue_label',
    ],
  },
  {
    key: 'restricted_access',
    defaultLabel: 'Restricted Access',
    fields: ['restricted_access.restricted_program_label'],
  },
  {
    key: 'position_assignments',
    defaultLabel: 'Position Assignments',
    fields: ['position_assignments.label'],
  },
  {
    key: 'notifications',
    defaultLabel: 'Notifications',
    fields: ['notifications.ie_kit_changes_label'],
  },
  {
    key: 'person_management',
    defaultLabel: 'Person Management',
    fields: [
      'identity.person_label',
      'identity.person_label_plural',
      'identity.individual_id_label',
      'identity.individual_id_prefix',
    ],
  },
  {
    key: 'email',
    defaultLabel: 'Email',
    fields: [],
  },
];

// ---------------------------------------------------------------------------
// Equipment configurations panel — side-nav with equipment type sub-tabs
// ---------------------------------------------------------------------------

/** Build helper text for a label key, using current form values for dynamic examples. */
const getLabelHelperText = (
  labelKey: string,
  form: Record<string, string>,
  compositeKey: string,
): string | undefined => {
  if (labelKey === 'individual_id_prefix') {
    const prefix = form[compositeKey] || 'INDV';
    return `Enter just the prefix code without a dash. Example: ${prefix} → identifiers display as ${prefix}-123456`;
  }
  return undefined;
};

/** Feature groups rendered inside the Individual Equipment form.
 * Tab labels are derived dynamically from saved config in the component. */
const IEM_FEATURE_KEYS = [
  'front_window',
  'identity',
  'ie_kits',
  'location_categories',
  'notifications',
  'issue_programs',
  'restricted_access',
  'position_assignments',
] as const;

// ---------------------------------------------------------------------------
// Survey email template editor — shown when the "Email" tab is active
// ---------------------------------------------------------------------------
const SURVEY_SKIP_KEY = 'front_window_survey_skipped';
const SURVEY_SKIP_LOCALE = 'en-US';

const SurveyEmailForm: React.FC = () => {
  const { data: templates, isLoading } = useEmailTemplates();
  const { mutateAsync: updateTemplate, isPending } = useUpdateEmailTemplate();
  const notifications = useNotifications();

  const template = useMemo(
    () =>
      templates
        ?.filter((t) => t.key === SURVEY_SKIP_KEY && t.locale === SURVEY_SKIP_LOCALE)
        .sort((a, b) => b.version - a.version)[0] ?? null,
    [templates],
  );

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  useEffect(() => {
    if (template) {
      setSubject(template.subject_template);
      setBody(template.body_template);
    }
  }, [template?.key, template?.locale, template?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty =
    !!template && (subject !== template.subject_template || body !== template.body_template);

  const handleSave = async () => {
    if (!template) return;
    try {
      await updateTemplate({
        key: template.key,
        locale: template.locale,
        version: template.version,
        subject_template: subject,
        body_template: body,
        enabled: template.enabled,
      });
      notifications.success('Survey email template saved');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save email template';
      notifications.error('Save failed', msg);
    }
  };

  if (isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={48} />
        <Skeleton variant="rounded" height={160} />
      </Stack>
    );
  }

  if (!template) {
    return (
      <Alert severity="warning">
        Survey email template not found. Run the latest database migrations to seed it.
      </Alert>
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>
          Survey Reminder Email
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sent to service members when an operator skips the in-person Customer Service Survey.
        </Typography>
      </Box>

      <Alert severity="info" icon={false}>
        <Typography variant="body2" fontWeight={600} gutterBottom>
          Available macros
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {['{{survey_link}}'].map((macro) => (
            <Chip
              key={macro}
              label={macro}
              size="small"
              variant="outlined"
              color="primary"
              sx={{ fontFamily: 'monospace', fontSize: 11 }}
            />
          ))}
        </Stack>
      </Alert>

      <TextField
        label="Email Subject"
        size="small"
        fullWidth
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
      />

      <TextField
        label="Email Body (HTML)"
        size="small"
        fullWidth
        multiline
        minRows={8}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
      />

      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          disabled={!isDirty || isPending}
          onClick={() => void handleSave()}
        >
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </Box>
    </Stack>
  );
};

/** Equipment sub-tabs — more types will be added here in the future. */
const EQUIPMENT_SUB_TABS: { key: string; label: string }[] = [
  { key: 'individual', label: 'Individual' },
];

/** Combined form for Individual Equipment settings (Front Window + Identity). */
const IndividualEquipmentForm: React.FC = () => {
  const { data: rows = [], isLoading, refetch } = useAdminConfigs('module.');
  const { mutateAsync: updateConfig } = useUpdateConfig();
  const notifications = useNotifications();

  const moduleRow = rows.find((r) => r.config_key === 'module.iem');
  const defaults = MODULE_DEFAULTS.iem ?? { display_name: 'Equipment', features: {} };
  const currentConfig = (moduleRow?.config_value as ModuleConfig | undefined) ?? defaults;

  // Build a flat form state keyed as "featureKey.labelKey"
  const [form, setForm] = useState<Record<string, string>>({});
  const [activeGroup, setActiveGroup] = useState<string>(IEM_FEATURE_KEYS[0]);

  // Derive tab labels from live form state so they update as the user types
  const iemFeatureGroups = useMemo(() => {
    return IEM_TAB_DEFS.map((tab) => ({
      ...tab,
      sectionLabel: (tab.labelFormKey && form[tab.labelFormKey]) || tab.defaultLabel,
    }));
  }, [form]);

  useEffect(() => {
    const state: Record<string, string> = {};
    for (const groupKey of IEM_FEATURE_KEYS) {
      const defaultLabels = (defaults.features[groupKey] ?? {}) as Record<string, string>;
      const dbLabels = (currentConfig.features?.[groupKey] ?? {}) as Record<string, string>;
      for (const [key, defaultVal] of Object.entries(defaultLabels)) {
        state[`${groupKey}.${key}`] = dbLabels[key] || defaultVal;
      }
    }
    setForm(state);
  }, [moduleRow?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerSave = useAutoSave(async () => {
    try {
      const updatedFeatures = { ...currentConfig.features };
      for (const groupKey of IEM_FEATURE_KEYS) {
        const groupLabels: Record<string, string> = {};
        for (const [compositeKey, value] of Object.entries(form)) {
          if (compositeKey.startsWith(`${groupKey}.`)) {
            groupLabels[compositeKey.slice(groupKey.length + 1)] = value;
          }
        }
        updatedFeatures[groupKey] = groupLabels;
      }
      const updated: ModuleConfig = {
        display_name: currentConfig.display_name,
        features: updatedFeatures,
      };
      await updateConfig({ configKey: 'module.iem', configValue: updated });
      notifications.success('Individual Equipment settings saved');
      refetch();
    } catch {
      notifications.error('Failed to save Individual Equipment settings');
    }
  });

  const handleChange = (compositeKey: string, value: string) => {
    setForm((prev) => ({ ...prev, [compositeKey]: value }));
    triggerSave();
  };

  if (isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={56} />
        <Skeleton variant="rounded" height={200} />
      </Stack>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5">Individual Equipment Settings</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Override the default label and workflow settings for Individual Equipment.
          </Typography>
          {activeGroup !== 'email' && <AutoSaveHint />}
        </Box>

        <Tabs
          value={activeGroup}
          onChange={(_e, v) => setActiveGroup(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {iemFeatureGroups.map((group) => (
            <Tab key={group.key} value={group.key} label={group.sectionLabel} />
          ))}
        </Tabs>

        {activeGroup === 'email' ? (
          <SurveyEmailForm />
        ) : (
          iemFeatureGroups
            .filter((group) => group.key === activeGroup)
            .map((group) => (
              <Stack key={group.key} spacing={2}>
                {group.fields.map((compositeKey) => {
                  const [featureKey = '', labelKey = ''] = compositeKey.split('.');
                  const defaultLabels = (defaults.features[featureKey] ?? {}) as Record<
                    string,
                    string
                  >;
                  const personLabel =
                    form['identity.person_label'] ||
                    (defaults.features.identity as Record<string, string> | undefined)
                      ?.person_label ||
                    'Service Member';
                  const displayLabel =
                    labelKey === 'menu_label_service_member'
                      ? `Navigation Menu Label (${personLabel})`
                      : (LABEL_DISPLAY_NAMES[labelKey] ?? labelKey);
                  return (
                    <TextField
                      key={compositeKey}
                      size="small"
                      fullWidth
                      label={displayLabel}
                      placeholder={defaultLabels[labelKey]}
                      helperText={getLabelHelperText(labelKey, form, compositeKey)}
                      value={form[compositeKey] ?? ''}
                      onChange={(e) => handleChange(compositeKey, e.target.value)}
                    />
                  );
                })}
              </Stack>
            ))
        )}
      </Stack>
    </Paper>
  );
};

const EquipmentConfigurationsPanel: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(EQUIPMENT_SUB_TABS[0]?.key ?? '');

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        mt: 2,
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      {/* Side navigation */}
      <Paper
        sx={{
          width: { xs: '100%', md: 220 },
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        <List disablePadding dense>
          {EQUIPMENT_SUB_TABS.map((tab) => (
            <ListItemButton
              key={tab.key}
              selected={selectedTab === tab.key}
              onClick={() => setSelectedTab(tab.key)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.50',
                  borderRight: 3,
                  borderColor: 'primary.main',
                },
              }}
            >
              <ListItemText
                primary={tab.label}
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: selectedTab === tab.key ? 600 : 400,
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Paper>

      {/* Content area */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {selectedTab === 'individual' && <IndividualEquipmentForm />}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Catalog Settings panel — direct label editor (no sub-tabs)
// ---------------------------------------------------------------------------

const CATALOG_LABEL_DISPLAY_NAMES: Record<string, string> = {
  available_stock_label: 'Available Stock Label',
  assigned_stock_label: 'Assigned Stock Label',
  primary_location_label: 'Primary Location Field Label',
  secondary_location_label: 'Secondary Location Field Label',
  reorder_point_column_label: 'Reorder Point Column Label (Dashboard)',
};

const CatalogConfigPanel: React.FC = () => {
  const { data: rows = [], isLoading, refetch } = useAdminConfigs('module.');
  const { mutateAsync: updateConfig } = useUpdateConfig();
  const notifications = useNotifications();

  const moduleRow = rows.find((r) => r.config_key === 'module.catalog');
  const defaults = MODULE_DEFAULTS.catalog ?? { display_name: 'Catalog', features: {} };
  const currentConfig = (moduleRow?.config_value as ModuleConfig | undefined) ?? defaults;
  const catalogFeature = (currentConfig.features?.catalog ??
    defaults.features?.catalog ??
    {}) as Record<string, string>;
  const locationLabelsFeature = (currentConfig.features?.location_labels ??
    defaults.features?.location_labels ??
    {}) as Record<string, string>;
  const dashboardLabelsFeature = (currentConfig.features?.dashboard_labels ??
    defaults.features?.dashboard_labels ??
    {}) as Record<string, string>;

  const [formState, setFormState] = useState<Record<string, string>>({});

  useEffect(() => {
    const defaultLabels = (defaults.features?.catalog ?? {}) as Record<string, string>;
    const defaultLocationLabels = (defaults.features?.location_labels ?? {}) as Record<
      string,
      string
    >;
    const defaultDashboardLabels = (defaults.features?.dashboard_labels ?? {}) as Record<
      string,
      string
    >;
    const state: Record<string, string> = {};
    for (const [key, defaultVal] of Object.entries(defaultLabels)) {
      state[key] = catalogFeature[key] || defaultVal;
    }
    for (const [key, defaultVal] of Object.entries(defaultLocationLabels)) {
      state[key] = locationLabelsFeature[key] || defaultVal;
    }
    for (const [key, defaultVal] of Object.entries(defaultDashboardLabels)) {
      state[key] = dashboardLabelsFeature[key] || defaultVal;
    }
    setFormState(state);
  }, [moduleRow?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerSave = useAutoSave(async () => {
    try {
      const catalogLabels: Record<string, string> = {};
      const locationLabels: Record<string, string> = {};
      const dashboardLabels: Record<string, string> = {};
      const catalogKeys = new Set(Object.keys(defaults.features?.catalog ?? {}));
      const dashboardKeys = new Set(Object.keys(defaults.features?.dashboard_labels ?? {}));
      for (const [key, value] of Object.entries(formState)) {
        if (catalogKeys.has(key)) {
          catalogLabels[key] = value;
        } else if (dashboardKeys.has(key)) {
          dashboardLabels[key] = value;
        } else {
          locationLabels[key] = value;
        }
      }
      const updated: ModuleConfig = {
        display_name: currentConfig.display_name,
        features: {
          ...currentConfig.features,
          catalog: catalogLabels,
          location_labels: locationLabels,
          dashboard_labels: dashboardLabels,
        },
      };
      await updateConfig({ configKey: 'module.catalog', configValue: updated });
      notifications.success('Catalog settings saved');
      refetch();
    } catch {
      notifications.error('Failed to save catalog settings');
    }
  });

  const handleChange = (key: string, value: string) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
    triggerSave();
  };

  if (isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={56} />
        <Skeleton variant="rounded" height={200} />
      </Stack>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Catalog Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Override the default label and workflow settings for Catalog.
      </Typography>
      <AutoSaveHint />
      <Stack spacing={2} sx={{ mt: 2 }}>
        {Object.entries(formState).map(([key, value]) => (
          <TextField
            key={key}
            size="small"
            fullWidth
            label={CATALOG_LABEL_DISPLAY_NAMES[key] ?? key}
            value={value}
            onChange={(e) => handleChange(key, e.target.value)}
          />
        ))}
      </Stack>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Distribution Settings panel — direct label editor (no sub-tabs)
// ---------------------------------------------------------------------------

const DISTRIBUTION_LABEL_DISPLAY_NAMES: Record<string, string> = {
  issue_from_default: 'Issue From Default',
};

const DistributionConfigPanel: React.FC = () => {
  const { data: rows = [], isLoading, refetch } = useAdminConfigs('module.');
  const { mutateAsync: updateConfig } = useUpdateConfig();
  const notifications = useNotifications();

  const moduleRow = rows.find((r) => r.config_key === 'module.distribution');
  const defaults = MODULE_DEFAULTS.distribution ?? { display_name: 'Distribution', features: {} };
  const currentConfig = (moduleRow?.config_value as ModuleConfig | undefined) ?? defaults;
  const feature = (currentConfig.features?.transfers_shipping ??
    defaults.features?.transfers_shipping ??
    {}) as Record<string, string>;

  const [formState, setFormState] = useState<Record<string, string>>({});

  useEffect(() => {
    const defaultLabels = (defaults.features?.transfers_shipping ?? {}) as Record<string, string>;
    const state: Record<string, string> = {};
    for (const [key, defaultVal] of Object.entries(defaultLabels)) {
      state[key] = feature[key] || defaultVal;
    }
    setFormState(state);
  }, [moduleRow?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerSave = useAutoSave(async () => {
    try {
      const updated: ModuleConfig = {
        display_name: currentConfig.display_name,
        features: { ...currentConfig.features, transfers_shipping: formState },
      };
      await updateConfig({ configKey: 'module.distribution', configValue: updated });
      notifications.success('Distribution settings saved');
      refetch();
    } catch {
      notifications.error('Failed to save distribution settings');
    }
  });

  const handleChange = (key: string, value: string) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
    triggerSave();
  };

  if (isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={56} />
        <Skeleton variant="rounded" height={200} />
      </Stack>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Distribution Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Override the default label and workflow settings for Distribution.
      </Typography>
      <AutoSaveHint />
      <Stack spacing={2} sx={{ mt: 2 }}>
        {Object.entries(formState).map(([key, value]) => (
          <TextField
            key={key}
            size="small"
            fullWidth
            label={DISTRIBUTION_LABEL_DISPLAY_NAMES[key] ?? key}
            value={value}
            onChange={(e) => handleChange(key, e.target.value)}
          />
        ))}
      </Stack>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// General Settings panel — Navigation sub-tab
// ---------------------------------------------------------------------------

const GeneralConfigPanel: React.FC = () => {
  const { data: rows = [], isLoading, refetch } = useAdminConfigs('module.');
  const { mutateAsync: updateConfig } = useUpdateConfig();
  const notifications = useNotifications();

  const moduleRow = rows.find((r) => r.config_key === 'module.general');
  const defaults = MODULE_DEFAULTS.general ?? { display_name: 'General', features: {} };
  const currentConfig = (moduleRow?.config_value as ModuleConfig | undefined) ?? defaults;
  const navFeature = (currentConfig.features?.navigation ??
    defaults.features?.navigation ??
    {}) as Record<string, string>;
  const navDefaults = (defaults.features?.navigation ?? {}) as Record<string, string>;

  const [formState, setFormState] = useState<Record<string, string>>({});

  useEffect(() => {
    const state: Record<string, string> = {};
    for (const [key, defaultVal] of Object.entries(navDefaults)) {
      state[key] = navFeature[key] || defaultVal;
    }
    setFormState(state);
  }, [moduleRow?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerSave = useAutoSave(async () => {
    try {
      const updated: ModuleConfig = {
        display_name: currentConfig.display_name,
        features: { ...currentConfig.features, navigation: formState },
      };
      await updateConfig({ configKey: 'module.general', configValue: updated });
      notifications.success('General settings saved');
      refetch();
    } catch {
      notifications.error('Failed to save general settings');
    }
  });

  const handleChange = (key: string, value: string) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
    triggerSave();
  };

  if (isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={56} />
        <Skeleton variant="rounded" height={200} />
      </Stack>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        General Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Configure application-wide settings.
      </Typography>

      {/* Navigation sub-section */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
        Navigation Labels
      </Typography>
      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        Navigation menu item labels can be customized per role. Service Members see their own label;
        all other roles share the default label.
      </Alert>
      <AutoSaveHint />

      {/* Dashboard menu item */}
      <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
        Dashboard Menu Item
      </Typography>
      <Stack spacing={2}>
        <TextField
          size="small"
          fullWidth
          label="Label — Everyone (default)"
          value={formState.dashboard_label_default ?? ''}
          onChange={(e) => handleChange('dashboard_label_default', e.target.value)}
          helperText="Displayed for Admin, User, Viewer, and Front Window roles"
        />
        <TextField
          size="small"
          fullWidth
          label="Label — Service Member"
          value={formState.dashboard_label_service_member ?? ''}
          onChange={(e) => handleChange('dashboard_label_service_member', e.target.value)}
          helperText="Displayed only for users with the Service Member role (default: Notifications)"
        />
      </Stack>

      {/* Transaction menu item */}
      <Typography variant="body2" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
        Transaction Menu Item
      </Typography>
      <Stack spacing={2}>
        <TextField
          size="small"
          fullWidth
          label="Label — Everyone (default)"
          value={formState.transaction_label_default ?? ''}
          onChange={(e) => handleChange('transaction_label_default', e.target.value)}
          helperText="Displayed for Admin, User, Viewer, and Front Window roles"
        />
        <TextField
          size="small"
          fullWidth
          label="Label — Service Member"
          value={formState.transaction_label_service_member ?? ''}
          onChange={(e) => handleChange('transaction_label_service_member', e.target.value)}
          helperText="Displayed only for users with the Service Member role"
        />
      </Stack>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Reports Settings panel — direct label editor (no sub-tabs)
// ---------------------------------------------------------------------------

const REPORTS_LABEL_DISPLAY_NAMES: Record<string, string> = {
  report_footer_text: 'Report Footer Text',
};

const ReportsConfigPanel: React.FC = () => {
  const { data: rows = [], isLoading, refetch } = useAdminConfigs('module.');
  const { mutateAsync: updateConfig } = useUpdateConfig();
  const notifications = useNotifications();

  const moduleRow = rows.find((r) => r.config_key === 'module.reports');
  const defaults = MODULE_DEFAULTS.reports ?? { display_name: 'Reports', features: {} };
  const currentConfig = (moduleRow?.config_value as ModuleConfig | undefined) ?? defaults;
  const feature = (currentConfig.features?.reports ?? defaults.features?.reports ?? {}) as Record<
    string,
    string
  >;

  const [formState, setFormState] = useState<Record<string, string>>({});

  useEffect(() => {
    const defaultLabels = (defaults.features?.reports ?? {}) as Record<string, string>;
    const state: Record<string, string> = {};
    for (const [key, defaultVal] of Object.entries(defaultLabels)) {
      state[key] = feature[key] || defaultVal;
    }
    setFormState(state);
  }, [moduleRow?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerSave = useAutoSave(async () => {
    try {
      const updated: ModuleConfig = {
        display_name: currentConfig.display_name,
        features: { ...currentConfig.features, reports: formState },
      };
      await updateConfig({ configKey: 'module.reports', configValue: updated });
      notifications.success('Reports settings saved');
      refetch();
    } catch {
      notifications.error('Failed to save reports settings');
    }
  });

  const handleChange = (key: string, value: string) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
    triggerSave();
  };

  if (isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={56} />
        <Skeleton variant="rounded" height={200} />
      </Stack>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Reports Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Override the default label and workflow settings for Reports.
      </Typography>
      <AutoSaveHint />
      <Stack spacing={2} sx={{ mt: 2 }}>
        {Object.entries(formState).map(([key, value]) => (
          <TextField
            key={key}
            size="small"
            fullWidth
            label={REPORTS_LABEL_DISPLAY_NAMES[key] ?? key}
            value={value}
            onChange={(e) => handleChange(key, e.target.value)}
          />
        ))}
      </Stack>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Supply Settings panel — direct label editor (no sub-tabs)
// ---------------------------------------------------------------------------

const SUPPLY_LABEL_DISPLAY_NAMES: Record<string, string> = {
  asset_authority_label: 'Asset Authority Label',
  asset_authority_default: 'Asset Authority Default',
  asset_authority_helper: 'Asset Authority Helper Text',
};

const SupplyConfigPanel: React.FC = () => {
  const { data: rows = [], isLoading, refetch } = useAdminConfigs('module.');
  const { mutateAsync: updateConfig } = useUpdateConfig();
  const notifications = useNotifications();

  const moduleRow = rows.find((r) => r.config_key === 'module.supply');
  const defaults = MODULE_DEFAULTS.supply ?? { display_name: 'Supply', features: {} };
  const currentConfig = (moduleRow?.config_value as ModuleConfig | undefined) ?? defaults;
  const feature = (currentConfig.features?.purchase_requisitions ??
    defaults.features?.purchase_requisitions ??
    {}) as Record<string, string>;

  const [formState, setFormState] = useState<Record<string, string>>({});

  useEffect(() => {
    const defaultLabels = (defaults.features?.purchase_requisitions ?? {}) as Record<
      string,
      string
    >;
    const state: Record<string, string> = {};
    for (const [key, defaultVal] of Object.entries(defaultLabels)) {
      state[key] = feature[key] || defaultVal;
    }
    setFormState(state);
  }, [moduleRow?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerSave = useAutoSave(async () => {
    try {
      const updated: ModuleConfig = {
        display_name: currentConfig.display_name,
        features: { ...currentConfig.features, purchase_requisitions: formState },
      };
      await updateConfig({ configKey: 'module.supply', configValue: updated });
      notifications.success('Supply settings saved');
      refetch();
    } catch {
      notifications.error('Failed to save supply settings');
    }
  });

  const handleChange = (key: string, value: string) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
    triggerSave();
  };

  if (isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={56} />
        <Skeleton variant="rounded" height={200} />
      </Stack>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Supply Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Override the default label and workflow settings for Supply.
      </Typography>
      <AutoSaveHint />
      <Stack spacing={2} sx={{ mt: 2 }}>
        {Object.entries(formState).map(([key, value]) => (
          <TextField
            key={key}
            size="small"
            fullWidth
            label={SUPPLY_LABEL_DISPLAY_NAMES[key] ?? key}
            value={value}
            onChange={(e) => handleChange(key, e.target.value)}
          />
        ))}
      </Stack>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Document configurations panel (side-tab navigation)
// Combines module-label document configs (DA 3161, DD 200, Contamination Memo)
// with system-configuration document configs (SF-1034, DA 4949).
// ---------------------------------------------------------------------------

/** Document label sub-tabs — module config feature keys shown in the doc panel. */
interface DocumentLabelDef {
  featureKey: string;
  label: string;
}

const DOCUMENT_LABEL_TABS: DocumentLabelDef[] = [
  { featureKey: 'contamination_memo', label: 'Contamination Memo' },
  { featureKey: 'dd200', label: 'DD 200' },
];

/** All sub-tab keys: label-based docs first, then system-config docs, all alphabetical. */
type DocTabKey = string;
interface DocTab {
  key: DocTabKey;
  label: string;
  hasLabels: boolean;
  hasConfig: boolean;
  isCustomPanel?: boolean;
}

const ALL_DOC_TABS: DocTab[] = (() => {
  const map = new Map<string, DocTab>();
  for (const d of DOCUMENT_LABEL_TABS) {
    map.set(d.featureKey, { key: d.featureKey, label: d.label, hasLabels: true, hasConfig: false });
  }
  for (const d of DOCUMENT_CONFIGS) {
    const existing = map.get(d.key);
    if (existing) {
      existing.hasConfig = true;
    } else {
      map.set(d.key, { key: d.key, label: d.label, hasLabels: false, hasConfig: true });
    }
  }
  // Add Document Storage as a custom panel
  map.set('document_storage', {
    key: 'document_storage',
    label: 'Document Storage',
    hasLabels: false,
    hasConfig: false,
    isCustomPanel: true,
  });
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
})();

/** Form for editing module.documents feature labels for a single document type. */
const DocumentLabelForm: React.FC<{ featureKey: string; label: string }> = ({
  featureKey,
  label,
}) => {
  const { data: rows = [], isLoading, refetch } = useAdminConfigs('module.');
  const { mutateAsync: updateConfig } = useUpdateConfig();
  const notifications = useNotifications();

  const moduleRow = rows.find((r) => r.config_key === 'module.documents');
  const defaults = MODULE_DEFAULTS.documents ?? { display_name: 'Documents', features: {} };
  const currentConfig = (moduleRow?.config_value as ModuleConfig | undefined) ?? defaults;
  const defaultLabels = (defaults.features[featureKey] ?? {}) as Record<string, string>;
  const dbLabels = (currentConfig.features?.[featureKey] ?? {}) as Record<string, string>;

  const [form, setForm] = useState<Record<string, string>>({});

  useEffect(() => {
    const state: Record<string, string> = {};
    for (const [key, defaultVal] of Object.entries(defaultLabels)) {
      state[key] = dbLabels[key] || defaultVal;
    }
    setForm(state);
  }, [moduleRow?.updated_at, featureKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerSave = useAutoSave(async () => {
    try {
      const updatedFeatures = { ...currentConfig.features, [featureKey]: form };
      const updated: ModuleConfig = {
        display_name: currentConfig.display_name,
        features: updatedFeatures,
      };
      await updateConfig({ configKey: 'module.documents', configValue: updated });
      notifications.success(`${label} settings saved`);
      refetch();
    } catch {
      notifications.error(`Failed to save ${label} settings`);
    }
  });

  const handleChange = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    triggerSave();
  };

  if (isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={56} />
        <Skeleton variant="rounded" height={200} />
      </Stack>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5">{label} Settings</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Override the default settings used for {label} documents.
          </Typography>
          <AutoSaveHint />
        </Box>

        <Alert severity="info" variant="outlined">
          Changes apply to newly generated documents only. Previously generated documents are not
          affected.
        </Alert>

        <Stack spacing={2}>
          {Object.entries(form).map(([labelKey, value]) => (
            <TextField
              key={labelKey}
              size="small"
              fullWidth
              label={LABEL_DISPLAY_NAMES[labelKey] ?? labelKey}
              value={value}
              onChange={(e) => handleChange(labelKey, e.target.value)}
            />
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Document Storage configuration panel
// ---------------------------------------------------------------------------

const DocumentStoragePanel: React.FC = () => {
  const notifications = useNotifications();
  const [config, setConfig] = useState({
    enabled: false,
    accountName: '',
    containerName: 'documents',
    connectionString: '',
    hasConnectionString: false,
  });
  const [migrationStatus, setMigrationStatus] = useState<{
    total: number;
    unmigrated: number;
    migrated: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [connectionStringInput, setConnectionStringInput] = useState('');

  // Parse AccountName from a connection string
  const parseAccountName = (connStr: string): string | null => {
    const match = connStr.match(/AccountName=([^;]+)/i);
    return match?.[1] ?? null;
  };

  const handleConnectionStringChange = (value: string) => {
    setConnectionStringInput(value);
    // Auto-populate account name when pasting a connection string
    const accountName = parseAccountName(value);
    if (accountName) {
      setConfig((prev) => ({ ...prev, accountName }));
    }
  };

  const fetchConfig = useCallback(async () => {
    try {
      const { documentStorageService } = await import('@/services/documentStorageService');
      const [cfg, status] = await Promise.all([
        documentStorageService.getConfig(),
        documentStorageService.getMigrationStatus(),
      ]);
      setConfig({ ...cfg, hasConnectionString: cfg.hasConnectionString ?? false });
      setMigrationStatus(status);
    } catch {
      notifications.error('Failed to load storage configuration');
    } finally {
      setLoading(false);
    }
  }, [notifications]);

  useEffect(() => {
    void fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    setTestResult(null);
    try {
      const { documentStorageService } = await import('@/services/documentStorageService');
      const updates: Record<string, unknown> = {
        accountName: config.accountName,
        containerName: config.containerName,
        enabled: config.enabled,
      };
      // Only send connection string if user typed a new one
      if (connectionStringInput) {
        updates.connectionString = connectionStringInput;
      }
      const updated = await documentStorageService.updateConfig(updates);
      setConfig({ ...updated, hasConnectionString: updated.hasConnectionString ?? false });
      setConnectionStringInput('');
      notifications.success('Storage configuration saved');
    } catch {
      notifications.error('Failed to save storage configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { documentStorageService } = await import('@/services/documentStorageService');
      const result = await documentStorageService.testConnection();
      setTestResult(result);
      if (result.success) {
        notifications.success('Connection successful');
      } else {
        notifications.error(`Connection failed: ${result.message}`);
      }
    } catch {
      setTestResult({ success: false, message: 'Failed to test connection' });
      notifications.error('Failed to test connection');
    } finally {
      setTesting(false);
    }
  };

  const handleMigrate = async () => {
    setMigrating(true);
    try {
      const { documentStorageService } = await import('@/services/documentStorageService');
      const result = await documentStorageService.migrateDocuments(20);
      notifications.success(result.message);
      // Refresh migration status
      const status = await documentStorageService.getMigrationStatus();
      setMigrationStatus(status);
    } catch {
      notifications.error('Migration batch failed');
    } finally {
      setMigrating(false);
    }
  };

  if (loading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={56} />
        <Skeleton variant="rounded" height={200} />
      </Stack>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudUpload color="primary" />
            <Typography variant="h5">Document Storage</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Configure Azure Blob Storage for generated documents. When enabled, new documents are
            stored in blob storage instead of the database, removing the 50 MB size limit.
          </Typography>
        </Box>

        {getEnvironmentBanner()?.env === 'development' && (
          <Alert severity="info" variant="outlined">
            <strong>Local Development:</strong> Use Azurite (Azure Storage Emulator). The connection
            string must use the in-cluster service name:{' '}
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title="Copy connection string">
                <IconButton
                  size="small"
                  aria-label="Copy connection string"
                  onClick={() => {
                    void navigator.clipboard.writeText(
                      'DefaultEndpointsProtocol=http;AccountName=devstoreaccount1;AccountKey=Eby8vdM02xNOcqFlqUwJPLlmEtlCDXJ1OUzFT50uSRZ6IFsuFq2UVErCz4I6tq/K1SZFPTOtr/KBHBeksoGMGw==;BlobEndpoint=http://equip-azurite:10000/devstoreaccount1;',
                    );
                    notifications.success('Connection string copied');
                  }}
                >
                  <ContentCopy sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <code style={{ fontSize: '0.75rem' }}>
                DefaultEndpointsProtocol=http;AccountName=devstoreaccount1;AccountKey=Eby8vdM02xNOcqFlqUwJPLlmEtlCDXJ1OUzFT50uSRZ6IFsuFq2UVErCz4I6tq/K1SZFPTOtr/KBHBeksoGMGw==;BlobEndpoint=http://equip-azurite:10000/devstoreaccount1;
              </code>
            </Box>
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          }}
        >
          <TextField
            label="Storage Account Name"
            helperText="Azure Storage Account name (e.g. equipstorage01) or 'devstoreaccount1' for Azurite"
            fullWidth
            value={config.accountName}
            onChange={(e) => setConfig((prev) => ({ ...prev, accountName: e.target.value }))}
          />
          <TextField
            label="Container Name"
            helperText="Blob container for generated documents"
            fullWidth
            value={config.containerName}
            onChange={(e) => setConfig((prev) => ({ ...prev, containerName: e.target.value }))}
          />
          <TextField
            label="Connection String"
            helperText={
              config.hasConnectionString
                ? 'A connection string is configured. Enter a new value to replace it.'
                : 'Azure Storage connection string — pasting will auto-populate the account name'
            }
            fullWidth
            value={
              connectionStringInput || (config.hasConnectionString ? config.connectionString : '')
            }
            onChange={(e) => handleConnectionStringChange(e.target.value)}
            placeholder={
              config.hasConnectionString ? '' : 'DefaultEndpointsProtocol=https;AccountName=...'
            }
            onFocus={() => {
              // Clear the masked value when user focuses the field to type a new one
              if (!connectionStringInput && config.hasConnectionString) {
                setConnectionStringInput('');
              }
            }}
            sx={{ gridColumn: '1 / -1' }}
            InputProps={{
              readOnly: !connectionStringInput && config.hasConnectionString ? false : undefined,
              sx:
                !connectionStringInput && config.hasConnectionString
                  ? { fontFamily: 'monospace', fontSize: '0.85rem' }
                  : undefined,
            }}
          />
        </Box>

        <Stack direction="row" spacing={2} alignItems="center">
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleTestConnection}
            disabled={testing || (!config.hasConnectionString && !connectionStringInput)}
          >
            {testing ? 'Testing...' : 'Test Connection'}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Chip
            label={config.enabled ? 'Enabled' : 'Disabled'}
            color={config.enabled ? 'success' : 'default'}
            onClick={async () => {
              const newEnabled = !config.enabled;
              setConfig((prev) => ({ ...prev, enabled: newEnabled }));
              try {
                const { documentStorageService } =
                  await import('@/services/documentStorageService');
                const updated = await documentStorageService.updateConfig({ enabled: newEnabled });
                setConfig({
                  ...updated,
                  hasConnectionString: updated.hasConnectionString ?? false,
                });
                notifications.success(`Blob storage ${newEnabled ? 'enabled' : 'disabled'}`);
              } catch {
                setConfig((prev) => ({ ...prev, enabled: !newEnabled }));
                notifications.error('Failed to update storage status');
              }
            }}
            disabled={saving}
            sx={{ cursor: 'pointer' }}
          />
        </Stack>

        {testResult && (
          <Alert severity={testResult.success ? 'success' : 'error'} variant="outlined">
            {testResult.message}
          </Alert>
        )}

        {/* Migration section */}
        {migrationStatus && migrationStatus.unmigrated > 0 && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Typography variant="h6">Document Migration</Typography>
              <Typography variant="body2" color="text.secondary">
                {migrationStatus.unmigrated} document{migrationStatus.unmigrated !== 1 ? 's' : ''}{' '}
                stored in the database can be migrated to blob storage.
                {migrationStatus.migrated > 0 && ` (${migrationStatus.migrated} already migrated)`}
              </Typography>
              <Box>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={handleMigrate}
                  disabled={migrating || !config.enabled}
                  startIcon={<CloudUpload />}
                >
                  {migrating ? 'Migrating...' : `Migrate Documents (batch of 20)`}
                </Button>
              </Box>
              {!config.enabled && (
                <Typography variant="caption" color="text.secondary">
                  Enable blob storage and save configuration before migrating.
                </Typography>
              )}
            </Stack>
          </Paper>
        )}

        {migrationStatus && migrationStatus.unmigrated === 0 && migrationStatus.migrated > 0 && (
          <Alert severity="success" variant="outlined">
            All {migrationStatus.migrated} documents have been migrated to blob storage.
          </Alert>
        )}
      </Stack>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// CIF Stamp upload section (rendered within the SF-1034 document tab)
// ---------------------------------------------------------------------------

const CifStampSection: React.FC = () => {
  const notifications = useNotifications();
  const { data, isLoading, error, refetch } = useCifStamp();
  const uploadMutation = useUploadCifStamp();
  const deleteMutation = useDeleteCifStamp();
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [placementPreviewOpen, setPlacementPreviewOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Build preview URL from selected file
  useEffect(() => {
    if (!pendingFile) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingFile]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file && file.type !== 'image/png') {
      notifications.error('Only PNG images are accepted for the CIF stamp.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file && file.size > 2 * 1024 * 1024) {
      notifications.error('Stamp image must be 2 MB or smaller.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setPendingFile(file);
  };

  const handleSave = async () => {
    const hasExistingStamp = !data?.isDefault;
    if (!pendingFile && !hasExistingStamp) return;

    if (pendingFile) {
      const reader = new FileReader();
      reader.onload = async () => {
        const dataUrl = reader.result as string;
        // Strip the "data:image/png;base64," prefix
        const base64 = dataUrl.split(',')[1] ?? '';
        try {
          await uploadMutation.mutateAsync({
            stampBase64: base64,
            filename: pendingFile.name,
          });
          notifications.success('CIF stamp saved successfully.');
          setPendingFile(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
          void refetch();
        } catch {
          notifications.error('Failed to save CIF stamp. Please try again.');
        }
      };
      reader.readAsDataURL(pendingFile);
    }
  };

  const handleRemove = async () => {
    try {
      await deleteMutation.mutateAsync();
      notifications.success('CIF stamp removed.');
      void refetch();
    } catch {
      notifications.error('Failed to remove CIF stamp. Please try again.');
    }
  };

  const currentStampSrc = data?.stampBase64 ? `data:image/png;base64,${data.stampBase64}` : null;

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        CIF Stamp
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload a PNG image of the CIF POSTED stamp to be composited onto generated SF-1034 vouchers.
        Leave empty to omit the stamp.
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {isLoading && <CircularProgress size={24} />}
      {error && <Alert severity="error">Failed to load CIF stamp configuration.</Alert>}

      {!isLoading && !error && (
        <Stack spacing={2}>
          {/* Current stamp preview */}
          {currentStampSrc && !pendingFile && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Current stamp{data?.filename ? ` — ${data.filename}` : ''}
              </Typography>
              <Box
                component="img"
                src={currentStampSrc}
                alt="CIF stamp"
                sx={{
                  maxHeight: 80,
                  maxWidth: 240,
                  objectFit: 'contain',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 0.5,
                  bgcolor: 'grey.50',
                }}
              />
            </Box>
          )}

          {data?.isDefault && !pendingFile && (
            <Alert severity="info" variant="outlined">
              No custom stamp uploaded. The SF-1034 will be generated without a CIF stamp.
            </Alert>
          )}

          {/* Pending file preview */}
          {previewUrl && pendingFile && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Preview — {pendingFile.name}
              </Typography>
              <Box
                component="img"
                src={previewUrl}
                alt="Preview"
                sx={{
                  maxHeight: 80,
                  maxWidth: 240,
                  objectFit: 'contain',
                  border: '1px solid',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                  p: 0.5,
                  bgcolor: 'grey.50',
                }}
              />
            </Box>
          )}

          {/* File picker */}
          <Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png"
              style={{ display: 'none' }}
              onChange={handleFileChange}
              id="cif-stamp-file-input"
            />
            <Button
              component="label"
              htmlFor="cif-stamp-file-input"
              variant="outlined"
              startIcon={<CloudUpload />}
              size="small"
            >
              {pendingFile ? 'Change file' : 'Select PNG'}
            </Button>
          </Box>

          {/* Actions */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              size="small"
              disabled={(!pendingFile && data?.isDefault) || uploadMutation.isPending}
              onClick={() => void handleSave()}
            >
              {uploadMutation.isPending ? 'Saving…' : 'Save stamp'}
            </Button>
            {(currentStampSrc ?? previewUrl) && (
              <Button variant="outlined" size="small" onClick={() => setPlacementPreviewOpen(true)}>
                Preview on SF-1034
              </Button>
            )}
            {!data?.isDefault && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                disabled={deleteMutation.isPending}
                onClick={() => void handleRemove()}
              >
                {deleteMutation.isPending ? 'Removing…' : 'Remove stamp'}
              </Button>
            )}
          </Stack>
        </Stack>
      )}

      {/* Stamp placement preview modal */}
      <Dialog
        open={placementPreviewOpen}
        onClose={() => setPlacementPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          SF-1034 Stamp Placement Preview
          <IconButton
            aria-label="close"
            onClick={() => setPlacementPreviewOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The CIF stamp will be applied at the bottom-left of the SF-1034 voucher. The grey area
            below represents a single SF-1034 page at reduced scale.
          </Typography>
          {/* SF-1034 page representation (8.5 × 11 aspect ratio at ~600 px wide) */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: 600,
              mx: 'auto',
              aspectRatio: '8.5 / 11',
              bgcolor: 'grey.100',
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            {/* Page title label */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                pointerEvents: 'none',
              }}
            >
              <Typography variant="h6" color="grey.400" fontWeight={700}>
                SF-1034
              </Typography>
              <Typography variant="caption" color="grey.400">
                (Page representation)
              </Typography>
            </Box>

            {/* Stamp at bottom-left — mirrors PDF compositing position */}
            <Box
              component="img"
              src={previewUrl ?? currentStampSrc ?? ''}
              alt="CIF stamp placement"
              sx={{
                position: 'absolute',
                bottom: '6%',
                left: '4%',
                width: '28%',
                objectFit: 'contain',
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlacementPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

const DocumentConfigurationsPanel: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(ALL_DOC_TABS[0]?.key ?? '');
  const activeTab = ALL_DOC_TABS.find((t) => t.key === selectedTab) ?? ALL_DOC_TABS[0]!;

  // Only fetch system-config rows when active tab has config fields
  const activeConfigDoc = activeTab.hasConfig
    ? DOCUMENT_CONFIGS.find((d) => d.key === activeTab.key)
    : null;
  const {
    data: rows = [],
    isLoading,
    error,
    refetch,
  } = useAdminConfigs(activeConfigDoc?.prefix ?? '__none__');

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        mt: 2,
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      {/* Side navigation */}
      <Paper
        sx={{
          width: { xs: '100%', md: 220 },
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        <List disablePadding dense>
          {ALL_DOC_TABS.map((tab) => (
            <ListItemButton
              key={tab.key}
              selected={selectedTab === tab.key}
              onClick={() => setSelectedTab(tab.key)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.50',
                  borderRight: 3,
                  borderColor: 'primary.main',
                },
              }}
            >
              <ListItemText
                primary={tab.label}
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: selectedTab === tab.key ? 600 : 400,
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Paper>

      {/* Content area */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {activeTab.isCustomPanel && activeTab.key === 'document_storage' && (
          <DocumentStoragePanel />
        )}
        {activeTab.hasLabels && (
          <DocumentLabelForm featureKey={activeTab.key} label={activeTab.label} />
        )}
        {activeTab.hasConfig &&
          (isLoading ? (
            <Stack spacing={2} sx={{ mt: activeTab.hasLabels ? 3 : 0 }}>
              <Skeleton variant="rounded" height={56} />
              <Skeleton variant="rounded" height={200} />
            </Stack>
          ) : error ? (
            <Alert severity="error" sx={{ mt: activeTab.hasLabels ? 3 : 0 }}>
              Failed to load configuration. Please try again.
            </Alert>
          ) : activeConfigDoc ? (
            <Box sx={{ mt: activeTab.hasLabels ? 3 : 0 }}>
              <DocumentConfigForm
                doc={activeConfigDoc}
                rows={rows}
                onSaved={() => {
                  void refetch();
                }}
              />
            </Box>
          ) : null)}
        {activeTab.key === 'sf1034' && <CifStampSection />}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Location type edit-label dialog
// ---------------------------------------------------------------------------

const EditLabelDialog: React.FC<{
  open: boolean;
  entity: Entity | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, entity, onClose, onSaved }) => {
  const { mutateAsync: updateEntity, isPending } = useUpdateEntity();
  const notifications = useNotifications();
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open && entity) {
      const ed = entity.entity_data as Record<string, unknown> | undefined;
      setLabel((ed?.type_name as string) ?? entity.display_name ?? '');
      setNotes((ed?.notes as string) ?? '');
    }
  }, [open, entity]);

  const handleSave = async () => {
    const trimmed = label.trim();
    if (!trimmed || !entity) return;

    try {
      const ed = { ...(entity.entity_data as Record<string, unknown>) };
      ed.type_name = trimmed;
      ed.notes = notes.trim() || null;
      await updateEntity({
        type: 'location_type',
        id: entity.id,
        patch: { display_name: trimmed, entity_data: ed },
      });
      notifications.success(`Type label updated to "${trimmed}"`);
      onSaved();
      onClose();
    } catch {
      notifications.error('Failed to update type label');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Edit Type Label
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            label="Type Label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            helperText="The name shown in the UI for this location type (e.g. Site, Row, Shelf)."
          />
          <TextField
            fullWidth
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            helperText="Administrative notes shown in the template card."
            multiline
            rows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={isPending || !label.trim()}>
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Location hierarchy templates panel
// ---------------------------------------------------------------------------

/** Derive a structural role chip label and colour from entity_data. */
function typeRoleLabel(entity: Entity): { label: string; color: 'primary' | 'default' } {
  const ed = entity.entity_data as Record<string, unknown> | undefined;
  const st = (ed?.structural_type as string) ?? '';
  if (st === 'root') return { label: 'Root', color: 'primary' };
  if (st === 'level') return { label: `Level ${ed?.structural_level ?? '?'}`, color: 'default' };
  return { label: st || '—', color: 'default' };
}

// ── Create Template dialog ────────────────────────────────────────────────

type TemplateLevelKey = 'root' | 'level1' | 'level2';

const templateLevelMeta: Record<
  TemplateLevelKey,
  { label: string; order: string; structuralType: 'root' | 'level'; structuralLevel: number | null }
> = {
  root: {
    label: 'Root',
    order: '10',
    structuralType: 'root',
    structuralLevel: null,
  },
  level1: {
    label: 'Level 1',
    order: '20',
    structuralType: 'level',
    structuralLevel: 1,
  },
  level2: {
    label: 'Level 2',
    order: '30',
    structuralType: 'level',
    structuralLevel: 2,
  },
};

const normalizeTemplateSlug = (name: string): string => {
  const slug = name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'TEMPLATE';
};

const buildLevelIdentifier = (templateName: string, level: TemplateLevelKey): string => {
  const slug = normalizeTemplateSlug(templateName);
  const suffix = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `LT-${slug}-${level.toUpperCase()}-${suffix}`;
};

const buildTemplateLevelEntityData = (
  level: TemplateLevelKey,
  templateId: string,
  selectedLevels: Record<TemplateLevelKey, boolean>,
): Record<string, unknown> => {
  const meta = templateLevelMeta[level];
  const terminalLevel: TemplateLevelKey = selectedLevels.level2
    ? 'level2'
    : selectedLevels.level1
      ? 'level1'
      : 'root';

  return {
    type_name: meta.label,
    display_order: meta.order,
    structural_type: meta.structuralType,
    structural_level: meta.structuralLevel,
    template_id: templateId,
    is_stockable: terminalLevel === level,
    notes: null,
  };
};

const CreateTemplateDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}> = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [levels, setLevels] = useState<Record<TemplateLevelKey, boolean>>({
    root: true,
    level1: true,
    level2: true,
  });
  const { mutateAsync: createEntity, isPending } = useCreateEntity();
  const notifications = useNotifications();

  const toggleLevel = (level: TemplateLevelKey) => {
    setLevels((prev) => {
      if (level === 'root') return prev;
      if (level === 'level1') {
        const nextLevel1 = !prev.level1;
        return {
          ...prev,
          level1: nextLevel1,
          level2: nextLevel1 ? prev.level2 : false,
        };
      }
      return { ...prev, level2: !prev.level2, level1: prev.level1 || !prev.level2 };
    });
  };

  const resetDialog = () => {
    setName('');
    setDescription('');
    setLevels({ root: true, level1: true, level2: true });
  };

  const handleCreate = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;
    if (!levels.root) {
      notifications.error('Root level is required');
      return;
    }
    if (levels.level2 && !levels.level1) {
      notifications.error('Level 2 requires Level 1');
      return;
    }

    try {
      const createdTemplate = await createEntity({
        entity_type: 'location_template',
        display_name: trimmedName,
        entity_data: { name: trimmedName, description: description.trim() || null },
      });

      const templateId = (createdTemplate as { id?: string } | undefined)?.id;
      if (!templateId) {
        throw new Error('Template was created but no template ID was returned.');
      }

      const selectedLevels = (Object.keys(levels) as TemplateLevelKey[]).filter((k) => levels[k]);
      for (const level of selectedLevels) {
        const label = templateLevelMeta[level].label;
        await createEntity({
          entity_type: 'location_type',
          entity_identifier: buildLevelIdentifier(trimmedName, level),
          display_name: label,
          entity_data: buildTemplateLevelEntityData(level, templateId, levels),
          status: 'active',
        });
      }

      notifications.success(`Template "${trimmedName}" created`);
      resetDialog();
      onCreated();
      onClose();
    } catch {
      notifications.error('Failed to create template');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Create Location Template
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            label="Template Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText="E.g. CIF, Kiosk, Training Area"
          />
          <TextField
            fullWidth
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
          />
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Template Levels
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked={levels.root} disabled size="small" />}
                label="Root"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={levels.level1}
                    onChange={() => toggleLevel('level1')}
                    size="small"
                  />
                }
                label="Level 1"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={levels.level2}
                    onChange={() => toggleLevel('level2')}
                    size="small"
                  />
                }
                label="Level 2"
              />
            </FormGroup>
            <Typography variant="caption" color="text.secondary">
              Root is always required. Level 2 requires Level 1.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            resetDialog();
            onClose();
          }}
        >
          Cancel
        </Button>
        <Button variant="contained" onClick={handleCreate} disabled={isPending || !name.trim()}>
          {isPending ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Single template card ─────────────────────────────────────────────────

interface TemplateCardProps {
  template: Entity;
  locationTypes: Entity[];
  onEditType: (entity: Entity) => void;
  onEditTemplate: (template: Entity, locationCount: number) => void;
  onRefresh: () => void;
  onDelete: (template: Entity) => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  locationTypes,
  onEditType,
  onEditTemplate,
  onRefresh,
  onDelete,
}) => {
  const navigate = useNavigate();
  const { mutateAsync: updateEntity } = useUpdateEntity();
  const notifications = useNotifications();

  // ── All locations for any type in this template ───────────────────────
  const { data: allLocations = [] } = useAllEntities({
    type: 'location',
    filters: { is_current: 'eq.true' },
  });

  // ── Template name inline edit ──────────────────────────────────────────
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(template.display_name ?? '');
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingName) nameInputRef.current?.select();
  }, [editingName]);

  const commitName = async () => {
    const trimmed = nameValue.trim();
    setEditingName(false);
    if (!trimmed || trimmed === template.display_name) return;
    try {
      const ed = { ...(template.entity_data as Record<string, unknown>) };
      ed.name = trimmed;
      await updateEntity({
        type: 'location_template',
        id: template.id,
        patch: { display_name: trimmed, entity_data: ed },
      });
      notifications.success(`Template renamed to "${trimmed}"`);
      onRefresh();
    } catch {
      notifications.error('Failed to rename template');
    }
  };

  // ── Type groups (deduplicated by structural position, tracking all IDs) ─
  const templateTypeGroups = useMemo(() => {
    const sorted = locationTypes
      .filter((lt) => {
        const ed = lt.entity_data as Record<string, unknown> | undefined;
        return ed?.template_id === template.id;
      })
      .sort((a, b) => {
        const aOrder = (a.entity_data as Record<string, unknown>)?.display_order;
        const bOrder = (b.entity_data as Record<string, unknown>)?.display_order;
        return String(aOrder ?? '999').localeCompare(String(bOrder ?? '999'));
      });

    const groups: Array<{ representative: Entity; allIds: Set<string> }> = [];
    const seen = new Map<string, number>();

    for (const lt of sorted) {
      const ed = lt.entity_data as Record<string, unknown> | undefined;
      const st = (ed?.structural_type as string) ?? lt.entity_identifier ?? '';
      const normSt = st === 'leaf' ? 'root' : st;
      const level = st === 'level' ? String(ed?.structural_level ?? '') : '';
      const key = `${normSt}|${level}`;
      if (seen.has(key)) {
        groups[seen.get(key)!]!.allIds.add(lt.id);
      } else {
        seen.set(key, groups.length);
        groups.push({ representative: lt, allIds: new Set([lt.id]) });
      }
    }
    return groups;
  }, [locationTypes, template.id]);

  // Locations per type group (full entities, not just counts)
  const locationsByGroup = useMemo(() => {
    return templateTypeGroups.map(({ allIds }) =>
      allLocations
        .filter((loc) => {
          const locTypeId = (loc.entity_data as Record<string, unknown>)?.location_type_id as
            | string
            | undefined;
          return locTypeId ? allIds.has(locTypeId) : false;
        })
        .sort((a, b) => (a.display_name ?? '').localeCompare(b.display_name ?? '')),
    );
  }, [templateTypeGroups, allLocations]);

  // Count locations per type group
  const locationCountByGroup = useMemo(
    () => locationsByGroup.map((locs) => locs.length),
    [locationsByGroup],
  );

  // Total count for delete guard
  const totalLocationCount = useMemo(
    () => locationCountByGroup.reduce((sum, n) => sum + n, 0),
    [locationCountByGroup],
  );

  // Modal state: which group index is open
  const [modalGroupIdx, setModalGroupIdx] = useState<number | null>(null);
  const modalLocations = modalGroupIdx !== null ? (locationsByGroup[modalGroupIdx] ?? []) : [];
  const modalTitle =
    modalGroupIdx !== null
      ? (templateTypeGroups[modalGroupIdx]?.representative.display_name ?? '')
      : '';

  // ── Per-type row ──────────────────────────────────────────────────────
  const TypeRow: React.FC<{
    lt: Entity;
    seq: number;
    isTerminal: boolean;
    locationCount: number;
    groupIdx: number;
  }> = useCallback(
    ({ lt, seq, isTerminal, locationCount: count, groupIdx }) => {
      const ed = lt.entity_data as Record<string, unknown> | undefined;
      const { label: roleLabel, color: roleColor } = typeRoleLabel(lt);
      return (
        <TableRow>
          <TableCell>{seq}</TableCell>
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {lt.display_name}
              </Typography>
              <Chip label={roleLabel} size="small" variant="outlined" color={roleColor} />
            </Box>
          </TableCell>
          <TableCell align="center">
            <Tooltip
              title={
                isTerminal
                  ? 'Terminal level — always stockable'
                  : 'Only the terminal level is stockable'
              }
            >
              <span>
                <Checkbox size="small" checked={isTerminal} disabled sx={{ p: 0 }} />
              </span>
            </Tooltip>
          </TableCell>
          <TableCell>
            <Typography variant="body2" color="text.secondary">
              {(ed?.notes as string) ?? ''}
            </Typography>
          </TableCell>
          <TableCell align="right">
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}
            >
              {count > 0 && (
                <Chip
                  label={count}
                  size="small"
                  variant="outlined"
                  onClick={() => setModalGroupIdx(groupIdx)}
                  sx={{ cursor: 'pointer', fontWeight: 600 }}
                />
              )}
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => onEditType(lt)} aria-label="Edit">
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </TableCell>
        </TableRow>
      );
    },
    [onEditType, setModalGroupIdx],
  );

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        {/* Template name + delete */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          {editingName ? (
            <TextField
              inputRef={nameInputRef}
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onBlur={() => void commitName()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void commitName();
                if (e.key === 'Escape') {
                  setNameValue(template.display_name ?? '');
                  setEditingName(false);
                }
              }}
              size="small"
              variant="outlined"
              label="Template Name"
              autoFocus
              sx={{ maxWidth: 300 }}
            />
          ) : (
            <>
              <Typography variant="h6" fontWeight={700}>
                Template: {template.display_name}
              </Typography>
              <Tooltip title="Rename template">
                <IconButton
                  size="small"
                  aria-label="Rename template"
                  onClick={() => {
                    setNameValue(template.display_name ?? '');
                    setEditingName(true);
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}

          <Box sx={{ ml: 'auto' }}>
            <Tooltip
              title={
                totalLocationCount > 0
                  ? `Cannot edit levels: ${totalLocationCount} location${totalLocationCount !== 1 ? 's' : ''} use this template`
                  : 'Edit template levels'
              }
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => onEditTemplate(template, totalLocationCount)}
                  disabled={totalLocationCount > 0}
                  aria-label="Edit template levels"
                >
                  <Settings fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                totalLocationCount > 0
                  ? `Cannot delete: ${totalLocationCount} location${totalLocationCount !== 1 ? 's' : ''} use this template`
                  : 'Delete template'
              }
            >
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={totalLocationCount > 0}
                  onClick={() => onDelete(template)}
                  aria-label="Delete template"
                >
                  <Delete fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48 }}>Seq</TableCell>
                <TableCell>Type label</TableCell>
                <TableCell sx={{ width: 90 }} align="center">
                  Stockable
                </TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right" sx={{ width: 80 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {templateTypeGroups.map(({ representative }, idx) => (
                <TypeRow
                  key={representative.id}
                  lt={representative}
                  seq={idx + 1}
                  isTerminal={idx === templateTypeGroups.length - 1}
                  locationCount={locationCountByGroup[idx] ?? 0}
                  groupIdx={idx}
                />
              ))}
              {templateTypeGroups.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      No location types assigned to this template.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Locations modal */}
      <Dialog
        open={modalGroupIdx !== null}
        onClose={() => setModalGroupIdx(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          {modalTitle} locations
          <IconButton size="small" onClick={() => setModalGroupIdx(null)} aria-label="Close">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {modalLocations.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No locations at this level.
            </Typography>
          ) : (
            <Stack spacing={0}>
              {modalLocations.map((loc) => (
                <Box
                  key={loc.id}
                  component="button"
                  onClick={() => {
                    navigate(`/location/${loc.id}`);
                    setModalGroupIdx(null);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    background: 'none',
                    border: 'none',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    px: 2,
                    py: 1.25,
                    textAlign: 'left',
                    width: '100%',
                    '&:hover': { bgcolor: 'action.hover' },
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                    {loc.display_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {loc.entity_identifier}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalGroupIdx(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const EditTemplateDialog: React.FC<{
  open: boolean;
  template: Entity | null;
  locationTypes: Entity[];
  locationCount: number;
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, template, locationTypes, locationCount, onClose, onSaved }) => {
  const notifications = useNotifications();
  const { mutateAsync: createEntity, isPending: isCreating } = useCreateEntity();
  const { mutateAsync: deleteEntity, isPending: isDeleting } = useDeleteEntity();
  const { mutateAsync: updateEntity, isPending: isUpdating } = useUpdateEntity();

  const [levels, setLevels] = useState<Record<TemplateLevelKey, boolean>>({
    root: true,
    level1: false,
    level2: false,
  });

  const existingByLevel = useMemo(() => {
    const map: Partial<Record<TemplateLevelKey, Entity>> = {};
    if (!template) return map;
    for (const lt of locationTypes) {
      const ed = lt.entity_data as Record<string, unknown> | undefined;
      if (ed?.template_id !== template.id) continue;
      const st = (ed?.structural_type as string) ?? '';
      const lvl = Number(ed?.structural_level ?? 0);
      if (st === 'root') map.root = lt;
      if (st === 'level' && lvl === 1) map.level1 = lt;
      if (st === 'level' && lvl === 2) map.level2 = lt;
    }
    return map;
  }, [locationTypes, template]);

  useEffect(() => {
    if (!open || !template) return;
    setLevels({
      root: true,
      level1: Boolean(existingByLevel.level1),
      level2: Boolean(existingByLevel.level2),
    });
  }, [open, template, existingByLevel]);

  const toggleLevel = (level: TemplateLevelKey) => {
    setLevels((prev) => {
      if (level === 'root') return prev;
      if (level === 'level1') {
        const nextLevel1 = !prev.level1;
        return {
          ...prev,
          level1: nextLevel1,
          level2: nextLevel1 ? prev.level2 : false,
        };
      }
      return { ...prev, level2: !prev.level2, level1: prev.level1 || !prev.level2 };
    });
  };

  const isPending = isCreating || isDeleting || isUpdating;

  const handleSave = async () => {
    if (!template) return;
    if (locationCount > 0) {
      notifications.error('Cannot edit template levels while locations are associated.');
      return;
    }
    if (levels.level2 && !levels.level1) {
      notifications.error('Level 2 requires Level 1');
      return;
    }

    try {
      const selected = (Object.keys(levels) as TemplateLevelKey[]).filter((k) => levels[k]);
      const terminalLevel: TemplateLevelKey = selected.includes('level2')
        ? 'level2'
        : selected.includes('level1')
          ? 'level1'
          : 'root';

      for (const level of Object.keys(templateLevelMeta) as TemplateLevelKey[]) {
        const existing = existingByLevel[level];
        const isSelected = selected.includes(level);

        if (isSelected && !existing) {
          await createEntity({
            entity_type: 'location_type',
            entity_identifier: buildLevelIdentifier(template.display_name ?? template.id, level),
            display_name: templateLevelMeta[level].label,
            entity_data: {
              ...buildTemplateLevelEntityData(level, template.id, levels),
              is_stockable: terminalLevel === level,
            },
            status: 'active',
          });
        }

        if (isSelected && existing) {
          const ed = { ...(existing.entity_data as Record<string, unknown> | undefined) };
          ed.is_stockable = terminalLevel === level;
          ed.template_id = template.id;
          await updateEntity({
            type: 'location_type',
            id: existing.id,
            patch: { entity_data: ed },
          });
        }

        if (!isSelected && existing && level !== 'root') {
          await deleteEntity({ id: existing.id, type: 'location_type' });
        }
      }

      notifications.success('Template levels updated');
      onSaved();
      onClose();
    } catch {
      notifications.error('Failed to update template levels');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit Template Levels</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {template?.display_name}
        </Typography>
        {locationCount > 0 ? (
          <Alert severity="warning">
            This template has {locationCount} associated location{locationCount !== 1 ? 's' : ''}.
            Remove associated locations before editing its levels.
          </Alert>
        ) : (
          <FormGroup>
            <FormControlLabel
              control={<Checkbox checked={levels.root} disabled size="small" />}
              label="Root"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={levels.level1}
                  onChange={() => toggleLevel('level1')}
                  size="small"
                />
              }
              label="Level 1"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={levels.level2}
                  onChange={() => toggleLevel('level2')}
                  size="small"
                />
              }
              label="Level 2"
            />
          </FormGroup>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => void handleSave()}
          disabled={isPending || locationCount > 0}
        >
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main panel ───────────────────────────────────────────────────────────

const LocationTemplatesPanel: React.FC = () => {
  const {
    data: templates = [],
    isLoading: loadingTemplates,
    error: templateError,
    refetch: refetchTemplates,
  } = useAllEntities({ type: 'location_template', filters: { is_current: 'eq.true' } });

  const { data: locationTypes = [], refetch: refetchTypes } = useAllEntities({
    type: 'location_type',
    filters: { is_current: 'eq.true' },
    order: 'entity_data->>display_order.asc',
  });

  const { mutateAsync: deleteEntity } = useDeleteEntity();
  const notifications = useNotifications();

  const [createOpen, setCreateOpen] = useState(false);
  const [editTypeTarget, setEditTypeTarget] = useState<Entity | null>(null);
  const [editTemplateTarget, setEditTemplateTarget] = useState<{
    template: Entity;
    locationCount: number;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Entity | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleRefetchAll = useCallback(() => {
    void refetchTemplates();
    void refetchTypes();
  }, [refetchTemplates, refetchTypes]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteEntity({ id: deleteTarget.id, type: 'location_template' });
      notifications.success(`Template "${deleteTarget.display_name}" deleted`);
      setDeleteTarget(null);
      handleRefetchAll();
    } catch {
      notifications.error('Failed to delete template');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Stack spacing={3}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h5">Location Hierarchy Templates</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Templates define the hierarchy of location types used within a subtree (e.g., CIF:
              Site → Row → Shelf). Root locations are associated with a template via their assigned
              location type.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateOpen(true)}
            sx={{ flexShrink: 0 }}
          >
            Create Template
          </Button>
        </Box>

        {loadingTemplates ? (
          <Skeleton variant="rounded" height={200} />
        ) : templateError ? (
          <Alert severity="error">Failed to load location templates.</Alert>
        ) : templates.length === 0 ? (
          <Alert severity="info">
            No location templates defined. Create a template to begin configuring your hierarchy.
          </Alert>
        ) : (
          templates.map((tmpl) => (
            <TemplateCard
              key={tmpl.id}
              template={tmpl}
              locationTypes={locationTypes}
              onEditType={setEditTypeTarget}
              onEditTemplate={(template, locationCount) =>
                setEditTemplateTarget({ template, locationCount })
              }
              onRefresh={handleRefetchAll}
              onDelete={setDeleteTarget}
            />
          ))
        )}
      </Stack>

      <CreateTemplateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleRefetchAll}
      />

      <EditLabelDialog
        open={!!editTypeTarget}
        entity={editTypeTarget}
        onClose={() => setEditTypeTarget(null)}
        onSaved={handleRefetchAll}
      />

      <EditTemplateDialog
        open={!!editTemplateTarget}
        template={editTemplateTarget?.template ?? null}
        locationTypes={locationTypes}
        locationCount={editTemplateTarget?.locationCount ?? 0}
        onClose={() => setEditTemplateTarget(null)}
        onSaved={handleRefetchAll}
      />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete template <strong>{deleteTarget?.display_name}</strong>?
            This will not delete any location types or locations.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDelete()}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Support configuration panel — degraded-state banner message
// ---------------------------------------------------------------------------

const SUPPORT_CONFIG: DocumentDef = {
  key: 'support',
  label: 'Support Contact',
  description:
    'Configure the message displayed to all users when the system is operating in a degraded state. This message appears as a banner at the top of every page.',
  prefix: 'support_',
  fields: [
    {
      configKey: 'support_degraded_message',
      label: 'Degraded State Message',
      helperText: 'The message shown to users when system health is degraded. Supports plain text.',
      multiline: true,
    },
  ],
};

const DEFAULT_DEGRADED_MESSAGE =
  'The eQuip Asset Management System is currently operating in a degraded state. Errors may occur when performing certain actions. Please contact eQuip support at https://support.equipsoftware.com/ for assistance.';

const SupportConfigPanel: React.FC = () => {
  const { data: rows = [], isLoading, error, refetch } = useAdminConfigs(SUPPORT_CONFIG.prefix);
  const { mutateAsync: updateConfig } = useUpdateConfig();
  const notifications = useNotifications();

  const currentValue = useMemo(() => {
    const row = rows.find((r) => r.config_key === 'support_degraded_message');
    return typeof row?.config_value === 'string' ? row.config_value : '';
  }, [rows]);

  const [message, setMessage] = useState('');

  useEffect(() => {
    setMessage(currentValue || DEFAULT_DEGRADED_MESSAGE);
  }, [currentValue]);

  const triggerSave = useAutoSave(async () => {
    try {
      await updateConfig({ configKey: 'support_degraded_message', configValue: message.trim() });
      notifications.success('Support message saved');
      void refetch();
    } catch {
      notifications.error('Failed to save support message');
    }
  });

  const handleChange = (value: string) => {
    setMessage(value);
    triggerSave();
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5">Support Contact Settings</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {SUPPORT_CONFIG.description}
            </Typography>
            <AutoSaveHint />
          </Box>

          <Alert severity="info" variant="outlined">
            This message is automatically displayed when the system detects degraded health. All
            users will see this banner until system health returns to normal.
          </Alert>

          {isLoading ? (
            <Skeleton variant="rounded" height={120} />
          ) : error ? (
            <Alert severity="error">Failed to load support configuration.</Alert>
          ) : (
            <>
              <TextField
                label="Degraded State Banner Message"
                helperText="The message shown to all users when system health is degraded."
                fullWidth
                multiline
                minRows={3}
                value={message}
                onChange={(e) => handleChange(e.target.value)}
              />

              <Alert
                severity="warning"
                variant="outlined"
                sx={{ '& .MuiAlert-message': { width: '100%' } }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Preview
                </Typography>
                <Typography variant="body2">{message || DEFAULT_DEGRADED_MESSAGE}</Typography>
              </Alert>
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main page — module-driven tab navigation
// ---------------------------------------------------------------------------

export const ConfigurationManagementPage: React.FC = () => {
  const navigate = useNavigate();
  usePageTitle('Configuration Management');
  const [selectedTab, setSelectedTab] = useState<ConfigTabKey>(CONFIG_TABS[0]!.key);

  return (
    <Container maxWidth={false} sx={{ mt: 2, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MUILink
            color="inherit"
            href="/"
            onClick={(event) => {
              event.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </MUILink>
          <Typography color="text.primary">Configuration Management</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Settings color="primary" sx={{ fontSize: 36 }} />
          <Typography variant="h4" component="h1">
            Configuration Management
          </Typography>
        </Box>
      </Box>

      {/* Module nav + content */}
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Left-side module tabs */}
        <Paper sx={{ width: { xs: '100%', md: 220 }, flexShrink: 0, overflow: 'hidden' }}>
          <Tabs
            value={selectedTab}
            onChange={(_, value) => setSelectedTab(value as ConfigTabKey)}
            orientation="vertical"
            indicatorColor="primary"
            textColor="primary"
            sx={{
              '& .MuiTab-root': {
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                textAlign: 'left',
                minHeight: 48,
                textTransform: 'none',
              },
            }}
          >
            {CONFIG_TABS.map((tab) => (
              <Tab
                key={tab.key}
                label={tab.label}
                value={tab.key}
                icon={tab.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Paper>

        {/* Content area — label editor + module-specific panel */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Module-specific functional panels */}
          {selectedTab === 'catalog' && <CatalogConfigPanel />}
          {selectedTab === 'distribution' && <DistributionConfigPanel />}
          {selectedTab === 'general' && <GeneralConfigPanel />}
          {selectedTab === 'iem' && <EquipmentConfigurationsPanel />}

          {selectedTab === 'reports' && <ReportsConfigPanel />}
          {selectedTab === 'supply' && <SupplyConfigPanel />}
          {selectedTab === 'documents' && <DocumentConfigurationsPanel />}
          {selectedTab === 'locations' && (
            <>
              <LocationTemplatesPanel />
              <ShelfMapConfigPanel />
            </>
          )}
          {selectedTab === 'support' && <SupportConfigPanel />}
        </Box>
      </Box>
    </Container>
  );
};
